import React from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { Toaster } from "sonner";
import { I18nProvider } from "@/lib/i18n";
import { AuthProvider } from "@/lib/auth";
import Layout from "@/components/Layout";
import ProtectedRoute from "@/components/ProtectedRoute";
import Home from "@/pages/Home";
import BrandProfile from "@/pages/BrandProfile";
import Compare from "@/pages/Compare";
import SectorBrowse from "@/pages/SectorBrowse";
import BrandsIndex from "@/pages/BrandsIndex";
import ReportError from "@/pages/ReportError";
import DppExplainer from "@/pages/DppExplainer";
import NotIndexed from "@/pages/NotIndexed";
import About from "@/pages/About";
import Methodology from "@/pages/Methodology";
import ForBrands from "@/pages/ForBrands";
import ConfirmFollow from "@/pages/ConfirmFollow";
import ConfirmNotify from "@/pages/ConfirmNotify";
import UnsubNotify from "@/pages/UnsubNotify";
import BrandClaim from "@/pages/BrandClaim";
import BrandSignIn from "@/pages/BrandSignIn";
import BrandDashboard from "@/pages/BrandDashboard";
import SectorReport from "@/pages/SectorReport";
import DataPage from "@/pages/DataPage";
import Embed from "@/pages/Embed";
import ConfirmSectorSubscription from "@/pages/ConfirmSectorSubscription";
import Unsubscribe from "@/pages/Unsubscribe";
import AdminLogin from "@/pages/admin/AdminLogin";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminBrandsList from "@/pages/admin/AdminBrandsList";
import AdminDeletedBrands from "@/pages/admin/AdminDeletedBrands";
import AdminBrandEdit from "@/pages/admin/AdminBrandEdit";
import AdminPartners from "@/pages/admin/AdminPartners";
import AdminReports from "@/pages/admin/AdminReports";
import AdminClaims from "@/pages/admin/AdminClaims";
import AdminSectorReports from "@/pages/admin/AdminSectorReports";
import AdminPipeline from "@/pages/admin/AdminPipeline";
import AdminApproval from "@/pages/admin/AdminApproval";
import AdminRunDetail from "@/pages/admin/AdminRunDetail";
import AdminInbox from "@/pages/admin/AdminInbox";
import AdminUsers from "@/pages/admin/AdminUsers";
import AdminBulkIntake from "@/pages/admin/AdminBulkIntake";
import AdminOps from "@/pages/admin/AdminOps";
import AcceptInvite from "@/pages/AcceptInvite";
import CrawlPolicy from "@/pages/CrawlPolicy";
import SiteGate from "@/components/SiteGate";
import "@/App.css";

function PublicLayoutWrapper({ children }) {
  const location = useLocation();
  const isAdmin = location.pathname.startsWith("/admin");
  const isEmbed = location.pathname.startsWith("/embed/");
  if (isAdmin || isEmbed) return children;
  return <Layout>{children}</Layout>;
}

export default function App() {
  return (
    <HelmetProvider>
      <SiteGate>
        <AuthProvider>
          <I18nProvider>
            <BrowserRouter>
            <PublicLayoutWrapper>
              <Routes>
                <Route path="/" element={<Home />} />
                <Route path="/brands" element={<BrandsIndex />} />
                <Route path="/brands/:slug" element={<BrandProfile />} />
                <Route path="/sectors/:code" element={<SectorBrowse />} />
                <Route path="/compare" element={<Compare />} />
                <Route path="/report-error" element={<ReportError />} />
                <Route path="/dpp-explainer" element={<DppExplainer />} />
                <Route path="/not-indexed" element={<NotIndexed />} />
                <Route path="/about" element={<About />} />
                <Route path="/methodology" element={<Methodology />} />
                <Route path="/for-brands" element={<ForBrands />} />
                <Route path="/confirm-follow" element={<ConfirmFollow />} />
                <Route path="/confirm-notify" element={<ConfirmNotify />} />
                <Route path="/unsub-notify" element={<UnsubNotify />} />
                <Route path="/confirm-sector-subscription" element={<ConfirmSectorSubscription />} />
                <Route path="/unsubscribe" element={<Unsubscribe />} />
                <Route path="/brands/:slug/claim" element={<BrandClaim />} />
                <Route path="/brand/sign-in" element={<BrandSignIn />} />
                <Route path="/brand/dashboard" element={<BrandDashboard />} />
                <Route path="/reports/:slug" element={<SectorReport />} />
                <Route path="/data" element={<DataPage />} />
                <Route path="/embed/:slug" element={<Embed />} />
                <Route path="/crawl-policy" element={<CrawlPolicy />} />

                <Route path="/admin/login" element={<AdminLogin />} />
                <Route path="/admin" element={<ProtectedRoute><AdminDashboard /></ProtectedRoute>} />
                <Route path="/admin/brands" element={<ProtectedRoute><AdminBrandsList /></ProtectedRoute>} />
                <Route path="/admin/deleted-brands" element={<ProtectedRoute><AdminDeletedBrands /></ProtectedRoute>} />
                <Route path="/admin/brands/:slug" element={<ProtectedRoute><AdminBrandEdit /></ProtectedRoute>} />
                <Route path="/admin/partners" element={<ProtectedRoute><AdminPartners /></ProtectedRoute>} />
                <Route path="/admin/reports" element={<ProtectedRoute><AdminReports /></ProtectedRoute>} />
                <Route path="/admin/claims" element={<ProtectedRoute><AdminClaims /></ProtectedRoute>} />
                <Route path="/admin/sector-reports" element={<ProtectedRoute><AdminSectorReports /></ProtectedRoute>} />
                <Route path="/admin/pipeline" element={<ProtectedRoute><AdminPipeline /></ProtectedRoute>} />
                <Route path="/admin/pipeline/runs/:runId" element={<ProtectedRoute><AdminRunDetail /></ProtectedRoute>} />
                <Route path="/admin/approvals" element={<ProtectedRoute><AdminApproval /></ProtectedRoute>} />
                <Route path="/admin/inbox" element={<ProtectedRoute><AdminInbox /></ProtectedRoute>} />
                <Route path="/admin/users" element={<ProtectedRoute><AdminUsers /></ProtectedRoute>} />
                <Route path="/admin/bulk-intake" element={<ProtectedRoute><AdminBulkIntake /></ProtectedRoute>} />
                <Route path="/admin/bulk-intake/:batchId" element={<ProtectedRoute><AdminBulkIntake /></ProtectedRoute>} />
                <Route path="/admin/accept-invite" element={<AcceptInvite />} />
                <Route path="/admin/ops" element={<ProtectedRoute><AdminOps /></ProtectedRoute>} />
              </Routes>
            </PublicLayoutWrapper>
            <Toaster position="top-right" />
          </BrowserRouter>
        </I18nProvider>
      </AuthProvider>
      </SiteGate>
    </HelmetProvider>
  );
}
